import { useState } from "react";
import { Moon } from "lucide-react";
import { MomentShell, SaveButton, EditLink } from "./MomentShell";
import { Chips, Field, DIFFICULTY, inputCls } from "./Chips";

const clean = (t) => (t || "").replace(/[.\s]+$/, "");

function finalOptions(plan) {
  const opts = [{ value: "completed:full", label: plan.reduced ? `Sì, anche la versione iniziale` : "Sì, l'ho svolta" }];
  if (plan.minimal) opts.push({ value: "completed:minimal", label: "Sì, la versione ridotta" });
  opts.push({ value: "not_completed", label: "No, oggi no" });
  return opts;
}

const STATUS_TEXT = {
  completed: "Passo completato",
  not_completed: "Passo non svolto oggi: domani riparti da qui",
};

export function EveningStep({ data, domanda, plan, checkin, evening, status, done, time, onSave }) {
  const confirmedAtNoon = checkin?.started === "done";
  const askFinal = !!plan && !confirmedAtNoon;
  const initialFinal = evening?.final === "completed" ? `completed:${evening.version || "full"}` : evening?.final || null;
  const [final, setFinal] = useState(initialFinal);
  const [noticed, setNoticed] = useState(evening?.noticed || "");
  const [tomorrow, setTomorrow] = useState(evening?.tomorrow || "");
  const [difficulty, setDifficulty] = useState(evening?.difficulty || checkin?.difficulty || null);
  const [share, setShare] = useState(!!evening?.share_coach);
  const [editing, setEditing] = useState(!done);
  const [saving, setSaving] = useState(false);

  const struggled = final === "not_completed";
  const current = plan ? clean(plan.reduced && plan.minimal ? plan.minimal : plan.action) : "";

  const save = async () => {
    setSaving(true);
    const payload = { noticed: noticed.trim(), tomorrow: tomorrow.trim(), share_coach: share };
    if (askFinal && final) {
      const [f, v] = final.split(":");
      payload.final = f;
      if (v) payload.version = v;
    }
    if (struggled && difficulty) payload.difficulty = difficulty;
    const ok = await onSave(payload);
    setSaving(false);
    if (ok) setEditing(false);
  };

  const canSave = askFinal ? !!final : !!(noticed.trim() || tomorrow.trim() || confirmedAtNoon);

  return (
    <MomentShell icon={Moon} label="Sera" time={time} title={data?.titolo || "Chiudi la giornata"} text={editing ? data?.app : null} done={done} index={2} testid="moment-sera">
      {!editing ? (
        <div className="mt-3" data-testid="evening-summary">
          {plan && <p className="a2g-read text-base text-[#E8E4D8]" data-testid="evening-status">{STATUS_TEXT[status] || "Giornata chiusa"}{status === "completed" ? `: ${clean(plan.minimal && (evening?.version === "minimal" || (!evening?.version && plan.reduced)) ? plan.minimal : plan.action)}` : ""}</p>}
          {evening?.tomorrow && <p className="mt-2 text-sm text-[#B4B4BC]">Porti a domani: {evening.tomorrow}</p>}
          <EditLink onClick={() => setEditing(true)} testid="evening-edit-button" />
        </div>
      ) : (
        <>
          {plan && (
            <p className="a2g-read mt-3 text-base text-[#B4B4BC]" data-testid="evening-plan-reminder">
              Il tuo passo di oggi: <span className="font-medium text-[#F4F4F5]">{current}</span>
              {plan.reduced && <span className="text-[#8A8E96]"> (ridotto da: {clean(plan.action)})</span>}
              {confirmedAtNoon && <span className="mt-1 block text-[#10B981]" data-testid="evening-confirmed-noon">Completato: lo hai confermato a mezzogiorno.</span>}
            </p>
          )}
          {askFinal && (
            <Field label="Alla fine, l'hai svolto?">
              <Chips options={finalOptions(plan)} value={final} onChange={setFinal} testid="evening-final" />
            </Field>
          )}
          {struggled ? (
            <Field label="Che cosa l'ha resa difficile? (facoltativo)">
              <Chips options={DIFFICULTY} value={difficulty} onChange={setDifficulty} testid="evening-difficulty" />
            </Field>
          ) : (
            <Field label={plan ? "Cosa hai notato? (facoltativo)" : "Domanda del giorno"}>
              {domanda && <p className="a2g-read mt-1 text-base text-[#F4F4F5]">{domanda}</p>}
              <textarea value={noticed} onChange={(e) => setNoticed(e.target.value)} rows={3} maxLength={1000} placeholder="Scrivi qui, se vuoi…" className={`${inputCls} resize-none`} data-testid="evening-noticed-input" />
            </Field>
          )}
          <Field label={struggled ? "Quale versione sarebbe realistica domani?" : "Cosa porti a domani? (facoltativo)"}>
            <input value={tomorrow} onChange={(e) => setTomorrow(e.target.value)} maxLength={300} placeholder="Una riga basta" className={inputCls} data-testid="evening-tomorrow-input" />
          </Field>
          <label className="mt-4 flex cursor-pointer items-center gap-3 text-sm text-[#B4B4BC]">
            <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} className="h-4 w-4 accent-[#D4AF37]" data-testid="evening-share-coach" />
            Coach Mino può leggere ciò che ho scritto
          </label>
          <SaveButton onClick={save} disabled={!canSave} saving={saving} label="Chiudi la giornata" testid="evening-save-button" />
        </>
      )}
    </MomentShell>
  );
}
